// Subscription plan constants

// Calculate yearly prices with 20% discount
const MONTHLY_STARTER_PRICE = 5.00;
const MONTHLY_CREATOR_PRICE = 15.00;
const MONTHLY_AGENCY_PRICE = 29.00;
const YEARLY_DISCOUNT = 0.2; // 20% discount

const YEARLY_STARTER_PRICE = +(MONTHLY_STARTER_PRICE * 12 * (1 - YEARLY_DISCOUNT)).toFixed(2);
const YEARLY_CREATOR_PRICE = +(MONTHLY_CREATOR_PRICE * 12 * (1 - YEARLY_DISCOUNT)).toFixed(2);
const YEARLY_AGENCY_PRICE = +(MONTHLY_AGENCY_PRICE * 12 * (1 - YEARLY_DISCOUNT)).toFixed(2);

export const BILLING_CYCLES = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
};

// Define Plan IDs consistently - Update these with your actual Stripe Product IDs
export const PLAN_IDS = {
  STARTER: 'prod_STARTER_ID', // Replace with actual Stripe Product ID for Starter
  CREATOR: 'prod_CREATOR_ID', // Replace with actual Stripe Product ID for Creator
  AGENCY: 'prod_AGENCY_ID'    // Replace with actual Stripe Product ID for Agency
};

// Use PLAN_IDS for PLANS export for backward compatibility if needed elsewhere,
// but prefer using PLAN_IDS directly for clarity.
export const PLANS = {
  STARTER: PLAN_IDS.STARTER,
  CREATOR: PLAN_IDS.CREATOR,
  AGENCY: PLAN_IDS.AGENCY
};

// Stripe Price IDs for dynamic checkout sessions
// TODO: Replace these with actual Stripe Price IDs from your Stripe Dashboard
export const STRIPE_PRICE_IDS = {
  [PLAN_IDS.STARTER]: {
    [BILLING_CYCLES.MONTHLY]: 'price_1RJvIrGKb7CWs1fgUBfGFAzi', // Replace with actual Price ID
    [BILLING_CYCLES.YEARLY]: 'price_1RXPwxGKb7CWs1fgtRetEaC6'    // Replace with actual Price ID
  },
  [PLAN_IDS.CREATOR]: {
    [BILLING_CYCLES.MONTHLY]: 'price_1RXInLGKb7CWs1fgaRZviZXo',  // Replace with actual Price ID
    [BILLING_CYCLES.YEARLY]: 'price_1RXPvUGKb7CWs1fgdDaphzr3'     // Replace with actual Price ID
  },
  [PLAN_IDS.AGENCY]: {
    [BILLING_CYCLES.MONTHLY]: 'price_1RXPyTGKb7CWs1fgoKkwDR5c',   // Replace with actual Price ID
    [BILLING_CYCLES.YEARLY]: 'price_1RXPyTGKb7CWs1fgoKkwDR5c'      // Replace with actual Price ID
  }
};

// Plan details keyed by Plan ID
export const planDetails = {
  [PLAN_IDS.STARTER]: {
    id: PLAN_IDS.STARTER,
    name: 'Starter',
    description: 'Essential features for growing accounts',
    price: {
      [BILLING_CYCLES.MONTHLY]: MONTHLY_STARTER_PRICE,
      [BILLING_CYCLES.YEARLY]: YEARLY_STARTER_PRICE
    },
    features: [
      '50 monthly posts',
      '1 Twitter post per day',
      '1 workspace',
      '500 AI credits',
      '5GB storage',
      '5 image generations per month',
      'Schedule 30 days in advance',
      'Unlimited scheduled posts',
      'Unlimited social accounts',
      'Customizable link in bio',
      'Caption & hashtag templates',
    ],
    highlighted: false
  },
  [PLAN_IDS.CREATOR]: {
    id: PLAN_IDS.CREATOR,
    name: 'Creator',
    description: 'Advanced features for content creators',
    price: {
      [BILLING_CYCLES.MONTHLY]: MONTHLY_CREATOR_PRICE,
      [BILLING_CYCLES.YEARLY]: YEARLY_CREATOR_PRICE
    },
    features: [
      'All Starter features',
      '250 monthly posts',
      '3 Twitter posts per day',
      '3 workspaces',
      '2000 AI credits',
      '20GB storage',
      '10 image generations per month',
      'Priority support'
    ],
    highlighted: true
  },
  [PLAN_IDS.AGENCY]: {
    id: PLAN_IDS.AGENCY,
    name: 'Agency',
    description: 'Complete solution for agencies and teams',
    price: {
      [BILLING_CYCLES.MONTHLY]: MONTHLY_AGENCY_PRICE,
      [BILLING_CYCLES.YEARLY]: YEARLY_AGENCY_PRICE
    },
    features: [
      'All Creator features',
      'Unlimited posts',
      'Unlimited Twitter posts per day',
      'Unlimited workspaces',
      '2500 AI credits',
      '25 image generations per month',
      '100GB storage',
      'Dedicated support'
    ],
    highlighted: false
  }
};

// Get Stripe Price ID for a plan ID and billing cycle
export const getStripePriceId = (planId, billingCycle = BILLING_CYCLES.MONTHLY) => {
  return STRIPE_PRICE_IDS[planId]?.[billingCycle] || null;
};

// Helper function to get plan details by ID and billing cycle
export const getPlanPrice = (planId, billingCycle = BILLING_CYCLES.MONTHLY) => {
  if (!planDetails[planId]) return 0;
  return planDetails[planId].price[billingCycle];
};

// Calculate savings for yearly plans based on plan ID
export const getYearlySavings = (planId) => {
  if (!planDetails[planId]) return 0;
  
  const monthlyPrice = planDetails[planId].price[BILLING_CYCLES.MONTHLY];
  const yearlyPrice = planDetails[planId].price[BILLING_CYCLES.YEARLY];
  
  return +(monthlyPrice * 12 - yearlyPrice).toFixed(2);
};

// Get yearly discount percentage
export const getYearlyDiscountPercentage = () => {
  return YEARLY_DISCOUNT * 100;
};
